import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { DataTable } from '../components/DataTable'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'

const useInvites = () =>
  useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invites')
        .select(`
          id, email, role, expires_at, used_at, created_at,
          households(name),
          profiles!invites_created_by_fkey(email)
        `)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

const StatusBadge = ({ invite }) => {
  if (invite.used_at)
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Used</span>
  if (new Date(invite.expires_at) < new Date())
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-background border border-border text-text-muted font-semibold">Expired</span>
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Pending</span>
}

export const InvitesPage = () => {
  const qc = useQueryClient()
  const { data: invites = [], isLoading } = useInvites()

  const revoke = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('invites')
        .update({ used_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  })

  const columns = [
    { key: 'email', label: 'Invited email', sortable: true },
    {
      key: 'household', label: 'Household',
      render: (_, row) => row.households?.name ?? '—',
    },
    { key: 'role', label: 'Role', sortable: true,
      render: (v) => <span className="capitalize">{v}</span>,
    },
    {
      key: 'created_by', label: 'Invited by',
      render: (_, row) => row.profiles?.email ?? '—',
    },
    {
      key: 'expires_at', label: 'Expires', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—',
    },
    {
      key: 'status', label: 'Status',
      render: (_, row) => <StatusBadge invite={row} />,
    },
    {
      key: 'actions', label: '',
      render: (_, row) => {
        if (row.used_at || new Date(row.expires_at) < new Date()) return null
        return (
          <button
            onClick={() => revoke.mutate(row.id)}
            disabled={revoke.isPending}
            className="text-xs text-rose-500 hover:text-rose-700 font-medium"
          >
            {revoke.isPending ? <Spinner size="sm" /> : 'Revoke'}
          </button>
        )
      },
    },
  ]

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Invites</h1>
        <p className="text-sm text-text-muted mt-1">
          {invites.filter((i) => !i.used_at && new Date(i.expires_at) > new Date()).length} pending
          &nbsp;·&nbsp; {invites.length} total
        </p>
      </div>
      <DataTable
        columns={columns}
        data={invites}
        loading={isLoading}
        emptyMessage="No invites found."
      />
    </div>
  )
}
